import React, { useContext } from "react";
import compose from "compose-function";
import moment from "moment";

import TableComponent from "../../components/TableComponent";
import CreateItemButton from "../../components/CreateItemButton";
import { addSearchParams } from "../../utils/dao";
import { useColumns } from "../../hooks/useColumns";
import useUserPermissionsContext from "../../hooks/useUserPermissionsContext";

import {
  getSortOrder,
  sortColumns,
  sortModelToString,
  fetchDataWithOptionsAndFilters,
  exportToFile,
} from "../../utils/utils";

import {
  addHrefToRow,
  changeDeletedToBoolean,
  addValueFormatter,
  padValueWith,
  readDataFromLocalStorage,
  getInitialFilters,
  getDefaultFilterModel,
  generateEntityKeyForLocalStorage,
  addFormatters,
} from "../../utils/table";

import { hardDelete, softDelete } from "../../dao/operations";

import { copyRecord, editRecords, exportRecords } from "../../dao/common";

import { FILTER_TYPES } from "../../dao/types";

import { getDefaultSort } from "../../constants/defaultSorting";

import UserRights from "../../enums/UserRights";
import { ROUTES } from "../../enums/Routes";
import { API_ROUTES } from "../../enums/api";

import { GlobalContext } from "../../globalContext";
import useEntities from "../../hooks/useEntities";
import { useSortModel } from "../../hooks/useSortModel";

const API_ROUTE = API_ROUTES["route-points"];

/**
 * Here we need to transform the row data retrieved,
 * in order to make it compatible with @mui/x-data-grid-pro
 */
const transformRows = (rows) => {
  return rows.reduce((acc, row) => {
    return [
      ...acc,
      compose(
        changeDeletedToBoolean("deleted"),
        addHrefToRow({
          field: "id",
          slug: "route-point",
        })
      )(row),
    ];
  }, []);
};

/**
 * Here we need to transform the column data retrieved,
 * in order to add @mui/x-data-grid-pro formatters
 */
export const transformColumns = (columns) => {
  return addFormatters(
    [
      { field: "object_number", formatter: addValueFormatter("object_number")(padValueWith(5)) },
      { field: "number", formatter: addValueFormatter("number")(padValueWith(4)) },
    ],
    columns);
};

export default function RoutePointsContainer() {
  const { globalFilters } = useContext(GlobalContext);

  // Get cached local filter model from local storage
  // with a fallback to default field values
  const cachedLocalFilter = readDataFromLocalStorage(
    generateEntityKeyForLocalStorage(FILTER_TYPES.route_point, "filterModel")
  ) ?? {
    filterModel: null,
    filterJSON: "",
  };

  // destructure fields from cachedLocalFilter
  const {
    filterModel: cachedLocalFilterModel,
    filterJSON: cachedFilterJSONString,
  } = cachedLocalFilter;

  const initialFilters = getInitialFilters({
    cache: {
      localFilterModel: cachedLocalFilterModel,
      filterJSONString: cachedFilterJSONString,
    },
    defaultFilterModel: getDefaultFilterModel("number").filterModel,
  });

  const { columns } = useColumns(FILTER_TYPES.route_point);

  const defaultSortString = getDefaultSort(FILTER_TYPES.route_point);

  const { sortModel, setSortModel } = useSortModel(FILTER_TYPES.route_point, {
    columns,
  });

  const sortModelString =
    sortModelToString(sortModel, columns) || defaultSortString;

  const {
    data,
    loadingState,
    fetchEntitiesData: fetchRoutePointsData,
    setFilterString,
    setOffset,
    limit,
    offset,
    setLimit,
    totalNumberOfRecords,
  } = useEntities("route-points", {
    params: {
      object_ids: globalFilters?.value ?? null,
    },
    initialFilters: initialFilters.apiFilterString,
    order: sortModelString,
  });

  const fetchRecordsDataWithGlobalFilters = fetchDataWithOptionsAndFilters(
    fetchRoutePointsData
  )({
    limit,
    offset,
    order: sortModelString,
    params: {
      object_ids: globalFilters?.value ?? null,
    },
  });

  const fetchExportData = async (filterString, selectedColumnsString) => {
    const abortController = new AbortController();
    const searchParams = {
      filters: filterString,
      columns: selectedColumnsString,
      object_ids: globalFilters?.value ?? null,
    };
    const response = await exportRecords(
      API_ROUTE,
      searchParams,
      abortController
    );

    exportToFile(
      `export_${FILTER_TYPES.route_point}_overview_${moment().format(
        "DD-MM-YYYY"
      )}.csv`,
      response
    );

    return "Export done";
  };

  const { isAllowedTo } = useUserPermissionsContext();

  const isAllowedToCreate = isAllowedTo(
    UserRights.CREATE,
    FILTER_TYPES.route_point
  );
  const isAllowedToDelete = isAllowedTo(
    UserRights.SOFT_DELETE,
    FILTER_TYPES.route_point
  );

  const isAllowedToExport = isAllowedTo(UserRights.EXPORT_CSV);
  const isAllowedToMassEdit = isAllowedTo(UserRights.MASS_EDIT_ROUTE_POINT);
  const isAllowedToInlineEdit = isAllowedTo(UserRights.INLINE_EDIT_ROUTE_POINT);

  const tableOptions = {
    hasDelete: isAllowedToDelete,
    sortOrder: getSortOrder(FILTER_TYPES.route_point),
    hasMassEdit: isAllowedToMassEdit,
    hasCopy: isAllowedToCreate,
    hasInlineEdit: isAllowedToInlineEdit,
  };

  const searchParams = new URLSearchParams(
    Object.entries({
      objectId: globalFilters?.value,
    }).filter(([, value]) => value)
  ).toString();

  return (
    <>
      <TableComponent
        {...{
          columns: transformColumns(sortColumns(columns)),
          transformColumns,
          loading: loadingState,
          rows: transformRows(data),
          refreshData: fetchRecordsDataWithGlobalFilters,
          renderCreateEntityButton: () => (
            <CreateItemButton
              to={addSearchParams(ROUTES.routePoints.create)(searchParams)}
              isDisabled={!isAllowedToCreate}
              entity={FILTER_TYPES.route_point}
            />
          ),
          copyEntry: copyRecord,
          editRecords,
          softDeleteEntry: softDelete({
            route: API_ROUTE,
            delete: true,
          }),
          hardDeleteEntry: hardDelete({
            route: API_ROUTE,
          }),
          restoreEntry: softDelete({
            route: API_ROUTE,
            delete: false,
          }),
          tableOptions,
          setFilters: setFilterString,
          setOffset,
          offset,
          setLimit,
          sortModel,
          setSortModel,
          numberOfRecords: totalNumberOfRecords,
          isAllowedToExport,
          entity: FILTER_TYPES.route_point,
          nestedEntity: false,
          initialFilterModel: initialFilters.grid,
          exportRecords: fetchExportData,
        }}
      />
    </>
  );
}
